import React, { useState } from 'react';
import { isAdmin } from '../services/api';
import Customer360 from './aiinfo/Customer360';
import GlobalKb from './aiinfo/GlobalKb';
import OpenQuestionsQueue from './aiinfo/OpenQuestionsQueue';

// Admin-only "AI Info" surface (AIINFO-v2.0). A tab shell over three sub-views:
//   • Customer 360 — the PRIMARY mode: search a customer, see/curate everything
//     the AI has learned or can derive for them (Q&A, nicknames, shorthand,
//     usual products, pallet counts, corrections log).
//   • Global knowledge — the v1 KB browse/curate (preserved intact).
//   • Open questions — the CENTRAL queue of everything the AI couldn't answer,
//     across all threads (the fix for "I can't find where to answer them").
//
// The isAdmin() early-return is preserved (App.tsx already gates the toggle;
// this is belt-and-suspenders per the engineering contract). Layout/styling
// mirrors the v1 / ApiInteractions vocabulary. App.tsx is intentionally NOT
// touched — the toggle + Brain icon are already wired from v1.

type TabKey = 'customer' | 'global' | 'questions';

const TABS: { key: TabKey; label: string }[] = [
  { key: 'customer', label: 'Customer 360' },
  { key: 'global', label: 'Global knowledge' },
  { key: 'questions', label: 'Open questions' },
];

const AIInfo: React.FC = () => {
  const [tab, setTab] = useState<TabKey>('customer');

  // UX-only admin gate (App.tsx never renders us for a non-admin — defensive).
  if (!isAdmin()) return null;

  return (
    <div className="flex-1 flex flex-col">
      {/* Sticky tab strip — sits at the same top offset as the v1 filter bar. */}
      <div className="bg-white px-3 sm:px-6 pt-3 sticky top-16 z-30 border-b border-gray-300">
        <div className="flex items-center gap-1">
          {TABS.map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={
                'px-4 py-2 text-sm font-medium rounded-t-lg transition-colors border-b-2 ' +
                (tab === t.key
                  ? 'border-brand-600 text-brand-700'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50')
              }
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {/* Active sub-view. Each owns its own load/error state and scroll area. */}
      {tab === 'customer' && <Customer360 />}
      {tab === 'global' && <GlobalKb />}
      {tab === 'questions' && <OpenQuestionsQueue />}
    </div>
  );
};

export default AIInfo;
